import { API, useEntity, useNotification } from 'connex-cds';
import React from 'react';
import { matchPath, Navigate, Outlet, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { FullStory } from './FullStory';
import { HeaderAndSidebar } from './HeaderAndSidebar';
import { IFrameAuthenticated } from './views/apps/entity-ref';
import { withVersionHandler } from './withVersionHandler';
import { useFeatureFlags } from './providers/feature-flags-provider';
import { FLAG_LOAD_ASSIGNMENTS } from './providers/feature-flags-provider/constants';
import { TrackitWrapper } from './trackit-wrapper/TrackitWrapper';
import { StandaloneWrapper } from './standalone-wrapper/StandaloneWrapper';
import MobileTicket from './views/apps/entity-ref/mobile-ticket/MobileTicket';
import { TruckNumber } from './views/apps/entity-ref/mobile-ticket/truck-number/TruckNumber';
import { PublicSummaryRoutes } from './views/apps/entity-ref/mobile-ticket/public-summary/PublicSummary';
import { TicketList } from './views/apps/entity-ref/mobile-ticket/ticket-list';
import { ActiveTicket } from './views/apps/entity-ref/mobile-ticket/active-ticket';
import { LoadAssignmentList } from './views/apps/entity-ref/mobile-ticket/load-assignments';
import { StandaloneLanding } from './views/apps/entity-ref/mobile-ticket/standalone-landing';

const FeatureFlaggedLoadAssignments = () => {
  const { entityRef } = useParams();
  const { getFlagValue, isLoading } = useFeatureFlags() ?? {};

  if (isLoading) {
    return null;
  }

  if (!getFlagValue?.(FLAG_LOAD_ASSIGNMENTS)) {
    return <Navigate to={entityRef ? `/${entityRef}/ticket-list` : '/'} replace />;
  }

  return <StandaloneWrapper />;
};

const RedirectToSelectedEntity = () => {
  const { entityRef } = useEntity();
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    if (!entityRef) return;

    // if the entityRef changes navigate to the same route with the new entityRef
    if (entityRef && !location.pathname.includes(entityRef)) {
      const matchWithEntityRef = matchPath(`/:entityRef/*`, location.pathname);

      if (matchWithEntityRef?.params?.entityRef) {
        const newPathname = matchWithEntityRef.pathname.replace(matchWithEntityRef.params.entityRef, entityRef);
        navigate(newPathname);
        return;
      }

      navigate(`/${entityRef}`);
    }
  }, [entityRef, location, navigate]);

  return (
    <IFrameAuthenticated>
      <Outlet />
    </IFrameAuthenticated>
  );
};

export const AppRoutes = () => {
  const notify = useNotification();

  React.useEffect(() => {
    API.setErrorHandler(msg => notify.error(msg));
  }, [notify]);

  return (
    <Routes>
      <Route path="/summary/:data/*" element={<PublicSummaryRoutes />} />
      <Route path="" element={<RedirectToSelectedEntity />}>
        <Route path=":entityRef/*" element={<IFrameAuthenticated />}>
          <Route element={<HeaderAndSidebar />}>
            <Route element={<FullStory />}>
              <Route path="" element={<StandaloneLanding />} exact />
            </Route>
            <Route path="driver/*" element={<TrackitWrapper />}>
              <Route path="" exact element={<TruckNumber />} />
              <Route path=":truckNumber/*" element={<MobileTicket driverExperience />} />
            </Route>
            <Route path="load-assignments/*" element={<FeatureFlaggedLoadAssignments />}>
              <Route path="" exact element={<LoadAssignmentList />} />
            </Route>
            <Route path="ticket-list/*" element={<StandaloneWrapper />}>
              <Route path="" exact element={<TicketList />} />
              <Route path=":truckNumber/*" element={<MobileTicket driverExperience selectedTicket={true} />} />
            </Route>
            <Route path="active-ticket/*" element={<StandaloneWrapper />}>
              <Route path="" exact element={<ActiveTicket />} />
              <Route path=":truckNumber/*" element={<MobileTicket driverExperience activeTicket={true} />} />
            </Route>
          </Route>
          <Route path=":truckNumber" element={<FullStory isOuterScript={true} />}>
            <Route path="mt/*" element={<MobileTicket />} />
          </Route>
        </Route>
      </Route>
    </Routes>
  );
};

export default withVersionHandler(AppRoutes);
